import { useEffect, useState } from 'react';
import {
  cardLabel,
  cardMeaning,
  cardName,
  type PublicParticipant,
  type PublicRoom,
  type VoteSummary,
} from '@planning-session/shared';
import { CheckIcon, SplitIcon } from './ui/Icon.js';
import { CardFace } from './ui/CardFace.js';
import { RoundClock } from './RoundClock.js';
import styles from './Table.module.css';

export interface TableProps {
  readonly room: PublicRoom;
}

type Highlight = 'none' | 'matched' | 'outlier';

/**
 * Once revealed, agreement gets a green edge and the extremes of a split get an
 * amber one — the two things a facilitator looks for first.
 */
function highlightFor(room: PublicRoom, vote: string | null): Highlight {
  if (room.phase !== 'revealed' || vote === null || room.summary === null) return 'none';
  if (room.summary.consensus) return 'matched';
  if (room.summary.min !== room.summary.max && (vote === room.summary.min || vote === room.summary.max)) {
    return 'outlier';
  }
  return 'none';
}

/** The number the room should act on: the agreed card, or the average. */
function verdictOf(summary: VoteSummary): { value: string; unit: string } {
  if (summary.consensus) return { value: cardLabel(summary.distribution[0]?.value ?? '—'), unit: 'agreed' };
  return { value: summary.average === null ? '—' : String(summary.average), unit: 'average' };
}

function Seat({ room, participant, index }: { room: PublicRoom; participant: PublicParticipant; index: number }) {
  const isSelf = participant.id === room.you?.id;
  const revealed = room.phase === 'revealed';
  const showsFace = participant.vote !== null;
  const highlight = highlightFor(room, participant.vote);
  const meaning = showsFace ? cardMeaning(room.deck, participant.vote as string) : null;

  const seatClasses = [styles.seat, participant.connected ? undefined : styles.awaySeat].filter(Boolean).join(' ');
  const slotClasses = [
    styles.cardSlot,
    !revealed && isSelf && showsFace ? styles.ownPreview : undefined,
    highlight === 'matched' ? styles.matched : undefined,
    highlight === 'outlier' ? styles.outlier : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={seatClasses}>
      {participant.hasVoted ? (
        <div className={slotClasses}>
          {/* Staggered so the table turns over like a dealer, not a light switch. */}
          <div
            className={`${styles.cardInner} ${revealed ? styles.flipped : ''}`}
            style={{ transitionDelay: revealed ? `${index * 90}ms` : '0ms' }}
          >
            <div className={styles.cardBack} aria-hidden="true" />
            {/*
             * The face carries the meaning under the value, and only ever the
             * face: a card back that hinted at what it costs would leak the
             * value it is hiding.
             */}
            <div
              className={styles.cardFace}
              aria-label={showsFace ? cardName(participant.vote as string, meaning) : undefined}
            >
              {showsFace && (
                <>
                  <span aria-hidden="true">
                    <CardFace value={participant.vote as string} iconSize={30} />
                  </span>
                  {meaning && (
                    <span className={styles.cardMeaning} aria-hidden="true">
                      {meaning.short}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={`${styles.empty} ${participant.connected ? styles.thinking : ''}`}>
          {participant.connected ? 'thinking' : 'away'}
        </div>
      )}

      <span className={styles.name}>{participant.name}</span>
      <span className={styles.tags}>
        {participant.isHost && <span className={styles.tag}>host</span>}
        {!participant.connected && <span className={`${styles.tag} ${styles.tagAway}`}>away</span>}
        {isSelf && <span className={styles.tag}>you</span>}
      </span>
    </div>
  );
}

/** The empty table asks for the one thing that moves the session forward. */
function Invitation({ roomId }: { roomId: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1_800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/room/${roomId}`);
      setCopied(true);
    } catch {
      // The code is on screen either way.
    }
  }

  return (
    <div className={styles.invite}>
      <div className={styles.inviteGhosts} aria-hidden="true">
        {[0, 1, 2, 3].map((n) => (
          <span key={n} className={styles.ghost} />
        ))}
      </div>
      <p className={styles.inviteTitle}>Nobody is holding cards yet</p>
      <span className={styles.inviteCode}>
        <span className={styles.inviteCodeValue}>{roomId}</span>
        <button
          type="button"
          className={`${styles.inviteCopy} ${copied ? styles.inviteCopied : ''}`}
          onClick={copyLink}
        >
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </span>
      <p className={styles.inviteHint}>
        Share the code and seats fill in as people arrive. Their cards stay face-down until you reveal.
      </p>
    </div>
  );
}

export function Table({ room }: TableProps) {
  const players = room.participants.filter((participant) => participant.role === 'player');
  const observers = room.participants.filter((participant) => participant.role === 'observer');
  const revealed = room.phase === 'revealed';
  const verdict = revealed && room.summary ? verdictOf(room.summary) : null;
  const share = room.playerCount === 0 ? 0 : room.votedCount / room.playerCount;
  // Nothing to look at yet: nobody holds cards, or you are the only one here and
  // have not played. Once you do play, your own card takes the table back.
  const alone = players.length === 0 || (room.participants.length <= 1 && room.votedCount === 0);

  return (
    <div className={styles.table}>
      {alone ? (
        <Invitation roomId={room.id} />
      ) : (
        <>
          <div className={styles.seats}>
            {players.map((participant, index) => (
              <Seat key={participant.id} room={room} participant={participant} index={index} />
            ))}
          </div>

          {observers.length > 0 && (
            <div className={styles.observers}>
              <span className={styles.observersLabel}>Watching</span>
              {observers.map((participant) => (
                <span key={participant.id} className={styles.observer}>
                  {participant.name}
                  {participant.isHost ? ' · host' : ''}
                </span>
              ))}
            </div>
          )}

          <div className={styles.rail}>
            {verdict && room.summary ? (
              <p className={`${styles.verdict} ${room.summary.consensus ? styles.agreed : styles.split}`}>
                <span className={styles.verdictIcon}>
                  {room.summary.consensus ? <CheckIcon size={18} /> : <SplitIcon size={18} />}
                </span>
                <span className={styles.verdictWord}>
                  {room.summary.consensus ? 'Consensus' : 'No consensus'}
                </span>
                <span className={styles.verdictValue}>{verdict.value}</span>
                <span className={styles.verdictUnit}>{verdict.unit}</span>
              </p>
            ) : (
              <>
                <span className={styles.progressText}>
                  {room.votedCount} of {room.playerCount} played
                </span>
                <span className={styles.progressTrack}>
                  <span className={styles.progressFill} style={{ transform: `scaleX(${share})` }} />
                </span>
                {/* The verdict owns the rail once revealed, so the clock only
                    appears while the round is still open. */}
                <RoundClock startedAt={room.round.startedAt} revealedAt={room.round.revealedAt} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
